/**
 * Runs a product tour from the registry in `~/constants/productTours`.
 *
 * Launched from a global notification's CTA (DEV-7438) — see
 * `useNotificationTourCta`, which every surface offering a tour goes through.
 * The backend stores only a `tour_key`, and everything else — destination,
 * steps, copy — comes from the registry.
 *
 * Three things this adds on top of driver.js:
 *
 * 1. **A launch gate that degrades.** driver.js's own `waitForElement` gives up
 *    quietly: on timeout it proceeds and (with `skipMissingElement: false`)
 *    highlights an invisible `driver-dummy-element` centred on the viewport,
 *    so a broken anchor becomes a popover pointing at nothing. That is a bad
 *    first impression right after the CTA navigated the user somewhere, so the
 *    first anchor is awaited here. If it never lands the tour is abandoned,
 *    the user is put on the feature's page anyway (most of the value of the
 *    CTA) and the problem is reported to Sentry. Later steps use the built-in
 *    waiter with `skipMissingElement`, so one dead anchor costs a step rather
 *    than the whole tour.
 * 2. **`prepare`/`revert` hooks.** driver.js can wait for an element but cannot
 *    make one appear. A step that lives inside a dialog needs something clicked
 *    first; `prepare` runs before the step is shown, and `revert` undoes it so
 *    Previous can get back out again.
 * 3. **Lifecycle analytics.** Which step each user reached, and whether they
 *    finished, closed it or wandered off — without which there is no way to
 *    tell whether tours are worth writing.
 *
 * Nothing here throws for a tour-shaped problem. The CTA acknowledges the
 * popup on its way out, so by the time a tour fails there is no notification
 * left to fall back to; a rejection would lose the user both.
 */
import type { Driver, DriveStep } from 'driver.js'
import { computed, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { ProductTourKey, ProductTourStep } from '~/constants/productTours'
import { PRODUCT_TOURS } from '~/constants/productTours'
import { recordEvent } from '~/observability/userEvents'
import { reportWarning } from '~/observability/warnings'
import { prefersReducedMotion } from '~/utils/motion'
import {
  DEFAULT_ANCHOR_TIMEOUT_MS,
  findAnchor,
  tourAnchorSelector,
} from '~/utils/tourAnchors'

/** Why a running tour ended, reported alongside `dismissedProductTour`. */
type TourEndReason =
  /** Escape, the ✕, the backdrop, or a caller stopping it on purpose */
  | 'closed'
  /** The user navigated somewhere the tour does not run */
  | 'navigated_away'
  /** The component hosting the tour unmounted */
  | 'left_page'
  /** Another tour was launched over the top of this one */
  | 'restarted'

/**
 * Loads driver.js on demand.
 *
 * Split out and dynamically imported so ~28KB of JS and CSS stays out of the
 * main bundle: a tour runs at most a handful of times per user, and never for
 * most of them. `import()` results are cached by the bundler, so a second tour
 * in the same session resolves from memory.
 *
 * The stylesheet is imported here rather than from a `src/modules/*` file
 * because Storybook does not install those — the overlay would render
 * unstyled in stories while looking fine in the app, the same trap
 * `vue-virtual-scroller`'s CSS has.
 */
async function loadDriver() {
  const [{ driver }] = await Promise.all([
    import('driver.js'),
    import('driver.js/dist/driver.css'),
  ])
  return driver
}

/**
 * Whether a tour key is one this build knows how to run.
 *
 * The backend column is free text, so a notification can name a tour that does
 * not exist here — a key the frontend has not implemented yet, or simply a
 * typo. Callers must gate their CTA on this, and should report the miss (see
 * `useNotificationTourCta`, which does both for every notification surface): a
 * hidden CTA is invisible to the admin who published the announcement.
 */
export function hasProductTour(key: string): key is ProductTourKey {
  return key in PRODUCT_TOURS
}

/**
 * How many tour launches currently hold the screen.
 *
 * Module-scoped because the code that needs the answer — `useAppModalPriority`,
 * which ranks the app's competing modals — holds no instance of this
 * composable, and there is more than one instance to hold: the forced popup
 * and the banner each own theirs through `useNotificationTourCta`.
 *
 * A count rather than a flag so a launch that is superseded or abandoned
 * releases only its own hold and never the one that replaced it.
 */
const activeTourLaunches = ref(0)

/**
 * Whether a product tour is on screen, or on its way there.
 *
 * "On its way there" is the load-bearing half. A tour is launched by
 * acknowledging the forced notification popup that offered it, and that
 * acknowledgement is the very thing holding the app's other modals back. Between
 * it and the first highlight sit the driver.js chunk download and up to
 * `DEFAULT_ANCHOR_TIMEOUT_MS` of waiting for the first anchor — a window a flag
 * raised only once driver.js is driving would leave wide open, which is exactly
 * where the extension install popup used to land on top of a running tour.
 */
export const productTourIsRunning = computed(() => activeTourLaunches.value > 0)

/**
 * Takes a hold on the screen for one launch, returning its idempotent release.
 *
 * Per launch rather than per composable: `startTour` can be re-entered while an
 * earlier launch of the same instance is still awaiting, and that earlier one
 * must not release the newer one's hold on its way out.
 */
function holdScreenForTour(): () => void {
  activeTourLaunches.value++
  let held = true
  return () => {
    if (!held) return
    held = false
    activeTourLaunches.value--
  }
}

export function useProductTour() {
  const router = useRouter()

  let activeDriver: Driver | undefined
  /**
   * Releases the screen hold belonging to `activeDriver`.
   *
   * Paired with it — set and cleared together — rather than left to the
   * `onDestroyed` hook alone, because driver.js skips that hook entirely when
   * it is destroyed before its first step ever highlighted (it only fires it
   * when there is an active element *and* an active step). Unmounting or
   * stopping a tour inside that window would otherwise strand the hold and
   * suppress every app-level modal for the rest of the session.
   */
  let releaseActiveTourScreen: (() => void) | undefined
  let stopRouteWatch: (() => void) | undefined
  /**
   * Index of the step the tour is currently moving to, while it is moving.
   *
   * Route changes are the tour's own (not the user leaving) for exactly as
   * long as this is set. It has to name the *target* step rather than be a
   * plain boolean: driver.js fires `onHighlighted` from a requestAnimationFrame
   * loop once the highlight animation finishes (~400ms), so the callback for
   * the step being *left* can land in the middle of the move it triggered. A
   * boolean would be cleared by that late callback and the in-flight
   * navigation would then be read as the user walking away — losing the tour
   * for anyone who clicks Next promptly.
   */
  let movingToStepIndex: number | undefined
  /** Index of the last step that made it on screen, if any. */
  let highlightedStepIndex: number | undefined
  /**
   * Why the tour currently being torn down is ending.
   *
   * A closure variable rather than an argument because driver.js's `destroy()`
   * takes none and its `onDestroyed` hook is handed driver's own state, not
   * ours — and that hook is the single place every ending passes through,
   * including the ones driver.js starts itself (Escape, the ✕, the backdrop,
   * finishing the last step), which leave this at its `'closed'` default.
   */
  let endReason: TourEndReason = 'closed'
  /**
   * Bumped by every launch and by teardown, to abandon an in-flight one.
   *
   * `endTour` can only stop a tour that already exists, and `activeDriver` is
   * not assigned until after the launch gate has awaited both the driver.js
   * chunk and the first anchor — up to `DEFAULT_ANCHOR_TIMEOUT_MS`. Unmounting
   * inside that window would otherwise leave `startTour` running against a
   * component that is gone, free to navigate or drop an overlay onto whatever
   * page the user moved to. Reachable today: the popup that owns the composable
   * is rendered behind `v-if="!userStore.demo_mode"`.
   */
  let launchGeneration = 0
  /** Set once the host component is gone, so no launch may resume after it. */
  let disposed = false

  /**
   * Whether the launch that owns `generation` should give up at this await
   * boundary — because the component went away, or a newer launch replaced it.
   */
  function isStaleLaunch(generation: number): boolean {
    return disposed || generation !== launchGeneration
  }

  function releaseRouteWatch(): void {
    stopRouteWatch?.()
    stopRouteWatch = undefined
  }

  /** Ends the running tour, if any. Safe to call when none is running. */
  function endTour(reason: TourEndReason): void {
    // Cleared before `destroy()` so the `onDestroyed` hook below doesn't
    // recurse back into here
    const running = activeDriver
    activeDriver = undefined
    releaseActiveTourScreen?.()
    releaseActiveTourScreen = undefined
    releaseRouteWatch()
    if (!running) return
    endReason = reason
    running.destroy()
  }

  /**
   * Puts the app into the state a step needs: navigate first, then run any
   * `prepare`, so a hook can act on the page it was routed to.
   */
  async function enterStep(step: ProductTourStep): Promise<void> {
    if (step.navigateTo) await router.push(step.navigateTo)
    await step.prepare?.()
  }

  function toDriveStep(
    key: ProductTourKey,
    step: ProductTourStep,
    index: number,
    stepCount: number,
    nextStep: ProductTourStep | undefined,
    getDriver: () => Driver
  ): DriveStep {
    return {
      element: tourAnchorSelector(step.anchor),
      // Covers targets that render late — a dialog animating in, a table
      // waiting on its API call
      waitForElement: DEFAULT_ANCHOR_TIMEOUT_MS,
      // Skipping is done below rather than by driver.js. Its own
      // `skipMissingElement` decides whether a step is skippable by looking
      // for the element *right now* — including while deciding what to label
      // the Next button — and in a tour that navigates, every later anchor is
      // legitimately absent at that moment. Turning it on made driver.js
      // conclude the tour was already over and render "Done" on step 1 of 6.
      skipMissingElement: false,
      onHighlighted: (element) => {
        // Only the step actually being moved to ends the move. A late callback
        // for a step we already left must not, or it would unguard a
        // navigation that is still in flight.
        if (movingToStepIndex === index) movingToStepIndex = undefined
        if (!element) {
          // driver.js gives up on `waitForElement` quietly: it highlights an
          // invisible dummy element in the centre of the viewport and passes
          // the hook `undefined`, which is the only signal that the anchor
          // never arrived. A popover pointing at nothing is worse than one
          // step less of tour, so move along — and report it, because that
          // anchor is missing for everyone who runs this tour, not just here.
          reportWarning('Product tour step anchor never appeared', {
            tourKey: key,
            anchor: step.anchor,
            stepIndex: index,
          })
          const runningDriver = getDriver()
          if (runningDriver.hasNextStep()) runningDriver.moveNext()
          else endTour('closed')
          return
        }
        highlightedStepIndex = index
        recordEvent('viewedProductTourStep', {
          tourKey: key,
          stepIndex: index,
          stepCount,
          stepAnchor: step.anchor,
        })
      },
      popover: {
        title: step.title,
        description: step.description,
        side: step.side,
        align: step.align,
        // Overriding `onNextClick` suppresses driver.js's own advance, so the
        // move has to be made by hand once `prepare` has run. Only attached
        // when there is something to prepare — otherwise the default (which
        // also handles the final "Done") is left alone.
        ...((nextStep?.prepare || nextStep?.navigateTo) && {
          onNextClick: async () => {
            movingToStepIndex = index + 1
            await enterStep(nextStep)
            getDriver().moveNext()
          },
        }),
        // Going back undoes what *this* step set up, which restores the state
        // the previous step ran in. The previous step's `prepare` is not
        // replayed — that state is precisely what it produced first time
        // round, and replaying it would re-open what we just closed.
        ...(step.revert && {
          onPrevClick: async () => {
            movingToStepIndex = index - 1
            await step.revert?.()
            getDriver().movePrevious()
          },
        }),
      },
    }
  }

  /**
   * Starts a tour, navigating to its page first if it declares `jumpToFeature`.
   *
   * Resolves either way: an unknown key or an anchor that never lands leaves
   * the user on the feature's page with a warning in Sentry, never with an
   * unhandled rejection.
   */
  async function startTour(key: ProductTourKey): Promise<void> {
    const tour = PRODUCT_TOURS[key]
    if (!tour) {
      // Unreachable through the popup, which gates on `hasProductTour` — but
      // `startTour` is public, and the registry is `Partial` precisely so that
      // keys can exist before their tours do
      reportWarning('No product tour registered for key', { tourKey: key })
      return
    }
    // Claimed before anything is awaited, so a launch that gets superseded or
    // unmounted part-way through can tell at each resumption point
    const generation = ++launchGeneration
    // A second launch replaces the first rather than stacking overlays
    endTour('restarted')
    movingToStepIndex = undefined
    highlightedStepIndex = undefined
    // Taken after the `endTour` above has released whatever the launch this one
    // replaces was still holding. The caller has already acknowledged away the
    // popup that offered this tour, so from here until it ends nothing else may
    // have the screen — including across every await below.
    const releaseScreen = holdScreenForTour()

    if (tour.jumpToFeature) await router.push(tour.featureRoute)
    if (isStaleLaunch(generation)) {
      releaseScreen()
      return
    }

    const [firstStep, ...laterSteps] = tour.steps
    await enterStep(firstStep)
    if (isStaleLaunch(generation)) {
      releaseScreen()
      return
    }

    // Both are latency the user is already sitting through, so they overlap:
    // the chunk downloads while the page the CTA navigated to mounts.
    const [driverFactory, anchor] = await Promise.all([
      loadDriver(),
      findAnchor(firstStep.anchor),
    ])
    // The longest await of the three, and the one the guard exists for
    if (isStaleLaunch(generation)) {
      releaseScreen()
      return
    }

    if (!anchor) {
      reportWarning(
        'Product tour could not start: first anchor never appeared',
        {
          tourKey: key,
          anchor: firstStep.anchor,
          timeoutMs: DEFAULT_ANCHOR_TIMEOUT_MS,
        }
      )
      // Landing them on the feature is most of what the CTA promised, and the
      // popup that offered it has already acknowledged itself away
      releaseScreen()
      await router.push(tour.featureRoute)
      return
    }

    const reducedMotion = prefersReducedMotion()
    const tourDriver: Driver = driverFactory({
      showProgress: tour.steps.length > 1,
      // A full-screen overlay that slides between targets is exactly the kind
      // of motion this preference is about, and the scroll it does to reach an
      // off-screen anchor is the other half of it
      animate: !reducedMotion,
      smoothScroll: !reducedMotion,
      overlayOpacity: 0.6,
      // driver.js disables Previous on the first step by itself
      showButtons: ['next', 'previous', 'close'],
      // Routes the endings driver.js starts by itself — Escape, the ✕, the
      // backdrop, Done on the last step — back through `endTour`, which
      // releases the hold rather than trusting `onDestroyed` to.
      //
      // It cannot be trusted here: driver.js fires `onDestroyed` only when it
      // has both an active element and an active step, and it records neither
      // until the highlight transition finishes — one frame later with
      // animation off, ~400ms later with it on, which is every user not on
      // reduced motion. Dismissing a tour inside that window stranded the
      // screen hold, and a stranded hold suppresses the onboarding dialog and
      // the install prompt for the rest of the session.
      //
      // Only driver.js's own endings arrive here; `destroy()` skips this hook,
      // so `endTour` calling it below is a teardown and not a recursion. That
      // also makes the teardown ours to finish: driver.js stops as soon as this
      // hook exists, so a hook that only released the hold would leave the
      // overlay on screen.
      onDestroyStarted: () => endTour('closed'),
      onDestroyed: () => {
        // Fires for every ending that got far enough for driver.js to have
        // highlighted a step; `onDestroyStarted` covers the rest
        activeDriver = undefined
        releaseScreen()
        releaseRouteWatch()
        recordTourEnded(key, tour.steps.length, endReason)
        endReason = 'closed'
      },
      steps: tour.steps.map((step, index) =>
        toDriveStep(
          key,
          step,
          index,
          tour.steps.length,
          tour.steps[index + 1],
          () => tourDriver
        )
      ),
    })
    activeDriver = tourDriver
    releaseActiveTourScreen = releaseScreen

    // Abandon the tour if the user navigates away mid-way — its anchors are
    // gone and the overlay would sit over an unrelated page.
    //
    // Path only, deliberately: query changes are in-page state, not leaving.
    // Settings rewrites `?tab=` as it mounts, and every `useRouteQuery` filter
    // pushes on change — reacting to those would kill the tour instantly.
    stopRouteWatch = router.afterEach((to, from) => {
      // Not until the tour is actually on screen. A redirect the app kicks off
      // independently (auth, onboarding, a default landing route) can land in
      // the few milliseconds between starting a tour and its first step
      // highlighting, and would otherwise kill the tour before it was ever
      // seen. Tours that jump to the feature never noticed this; guided ones,
      // which start wherever the user already is, hit it immediately.
      if (highlightedStepIndex === undefined) return
      // ...and not when the tour is the one navigating: a guided tour walks
      // itself from the nav into Settings, a path change it must survive
      if (movingToStepIndex === undefined && to.path !== from.path) {
        endTour('navigated_away')
      }
    })

    // A single-step tour is a feature highlight, which driver.js models
    // separately: no progress text and no next/previous buttons
    if (laterSteps.length === 0) {
      tourDriver.highlight(
        toDriveStep(key, firstStep, 0, 1, undefined, () => tourDriver)
      )
    } else {
      tourDriver.drive()
    }
  }

  /**
   * Records how a tour ended.
   *
   * "Completed" is reaching the last step rather than clicking Done
   * specifically — overriding `onDoneClick` would suppress driver.js's own
   * teardown and buy nothing, and someone who reads the final step and hits
   * Escape has had the whole tour either way.
   */
  function recordTourEnded(
    key: ProductTourKey,
    stepCount: number,
    reason: TourEndReason
  ): void {
    // Nothing was ever on screen — a restart racing the launch gate, say.
    // Reporting that as a dismissal would make every tour look abandoned.
    if (highlightedStepIndex === undefined) return
    const reachedLastStep = highlightedStepIndex === stepCount - 1
    recordEvent(
      reachedLastStep ? 'completedProductTour' : 'dismissedProductTour',
      {
        tourKey: key,
        stepIndex: highlightedStepIndex,
        stepCount,
        ...(reachedLastStep ? {} : { reason }),
      }
    )
    highlightedStepIndex = undefined
  }

  /** Ends the running tour, if any. Safe to call when none is running. */
  function stopTour(): void {
    endTour('closed')
  }

  onUnmounted(() => {
    // Order matters: invalidate any in-flight launch before tearing down, so a
    // gate still awaiting cannot resume and rebuild what `endTour` just cleared
    disposed = true
    launchGeneration++
    endTour('left_page')
  })

  return { startTour, stopTour }
}
